import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { PageShell } from './layout';
import { logEvent } from '../analytics';
import FaIcon from './FaIcon';
import styles from '../styles/InstallLandingPage.module.css';
import {
  detectEnvironment,
  setupInstallPromptListener,
  removeInstallPromptCallback,
  isInstallPromptAvailable,
  triggerInstall,
  getInstallStrategy
} from '../utils/installHelper';

// Benefits data for the landing page
const BENEFITS = [
  {
    icon: 'fa-fire',
    title: 'Mantieni la tua streak',
    description: 'Gioca giornaliere per non saltare mai la tua pratica. La costanza è la chiave per migliorare.',
    gradient: 'linear-gradient(145deg, #ff6b35, #f7931e)'
  },
  {
    icon: 'fa-bolt',
    title: 'Accesso istantaneo',
    description: 'Un tap dalla home e sei subito pronto a giocare. Niente browser, niente distrazioni.',
    gradient: 'linear-gradient(145deg, #1E66FF, #0A4ACC)'
  },
  {
    icon: 'fa-expand',
    title: 'Esperienza immersiva',
    description: 'Schermo intero senza barre del browser. Solo tu e il gioco, per la massima concentrazione.',
    gradient: 'linear-gradient(145deg, #10b981, #059669)'
  }
];

/**
 * InstallPage Component
 * 
 * A landing page that convinces users to install the app,
 * highlighting benefits for building daily habits.
 */
const InstallPage = () => {
  const [env, setEnv] = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [installAvailable, setInstallAvailable] = useState(false);
  const [installing, setInstalling] = useState(false);
  const hasTrackedShown = useRef(false);
  const envRef = useRef(null);

  // Initialize environment detection and prompt listener
  useEffect(() => {
    const detectedEnv = detectEnvironment();
    setEnv(detectedEnv);
    envRef.current = detectedEnv;

    const promptAvailable = isInstallPromptAvailable();
    setInstallAvailable(promptAvailable);

    const initialStrategy = getInstallStrategy(detectedEnv, promptAvailable);
    setStrategy(initialStrategy);

    const handleInstallAvailable = () => {
      setInstallAvailable(true);
      if (envRef.current) {
        const newStrategy = getInstallStrategy(envRef.current, true);
        setStrategy(newStrategy);
      }
    };

    setupInstallPromptListener(handleInstallAvailable);

    return () => {
      removeInstallPromptCallback(handleInstallAvailable);
    };
  }, []);

  // Track page view
  useEffect(() => {
    if (strategy && !hasTrackedShown.current) {
      hasTrackedShown.current = true;
      logEvent('install_page_view', {
        env: env?.label,
        strategy: strategy?.type
      });
    }
  }, [strategy, env]);

  // Handle install button click
  const handleInstallClick = async () => {
    if (installing) return;

    setInstalling(true);
    logEvent('install_page_click', { env: env?.label });

    try {
      const outcome = await triggerInstall();
      logEvent('install_page_result', { env: env?.label, outcome });
    } catch (err) {
      console.warn('[InstallPage] Install error:', err);
    } finally {
      setInstalling(false);
    }
  };

  // Scroll to instructions section
  const scrollToInstructions = () => {
    document.getElementById('instructions')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Determine the action verb based on strategy
  // Returns both imperative (for buttons) and infinitive (for questions)
  const getActionVerb = () => {
    if (!strategy) return { imperative: 'Installa', infinitive: 'installare' };
    if (strategy.type === 'bookmarkShortcut') return { imperative: 'Salva', infinitive: 'salvare' };
    if (strategy.type === 'shareSheetInstructions' && env?.isIOS) return { imperative: 'Aggiungi', infinitive: 'aggiungere' };
    return { imperative: 'Installa', infinitive: 'installare' };
  };

  const actionVerb = getActionVerb();

  return (
    <PageShell>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        {/* Animated stars */}
        <div className={styles.starsContainer}>
          {[...Array(12)].map((_, i) => (
            <div key={i} className={styles.star} />
          ))}
        </div>

        {/* Floating icons */}
        <div className={styles.floatingIcon} style={{ left: '8%' }}>🔥</div>
        <div className={styles.floatingIcon} style={{ left: '25%' }}>📅</div>
        <div className={styles.floatingIcon} style={{ left: '55%' }}>⭐</div>
        <div className={styles.floatingIcon} style={{ left: '80%' }}>🎯</div>

        <div className={styles.heroContent}>
          <div className={styles.iconContainer}>
            <div className={styles.appIconWrapper}>
              <FaIcon className={`fa-solid fa-gamepad ${styles.appIcon}`} />
            </div>
          </div>

          <p className={styles.heroSubtext}>Giochini di Parole</p>
          
          <h1 className={styles.heroTitle}>
            La costanza<br />
            <span>è il segreto</span>
          </h1>

          <p className={styles.heroDescription}>
            Costruisci l'abitudine di allenare la mente ogni giorno.
            {actionVerb.imperative === 'Installa' && ' Installa l\'app'} 
            {actionVerb.imperative === 'Salva' && ' Salva questa pagina'}
            {actionVerb.imperative === 'Aggiungi' && ' Aggiungi alla home'}
            {' '}per non perdere mai la tua streak.
          </p>

          <div className={styles.heroCta}>
            {strategy?.type === 'pwaPrompt' && installAvailable ? (
              <button
                className={styles.ctaButton}
                onClick={handleInstallClick}
                disabled={installing}
              >
                {installing ? (
                  <>
                    <FaIcon className="fa-solid fa-spinner fa-spin" />
                    <span>Installazione...</span>
                  </>
                ) : (
                  <>
                    <FaIcon className="fa-solid fa-download" />
                    <span>{actionVerb.imperative} ora</span>
                  </>
                )}
              </button>
            ) : (
              <button
                className={styles.ctaButton}
                onClick={scrollToInstructions}
              >
                <FaIcon className="fa-solid fa-arrow-down" />
                <span>Scopri come</span>
              </button>
            )}
          </div>

          <div className={styles.scrollHint}>
            <span>Scopri i vantaggi</span>
            <FaIcon className={`fa-solid fa-chevron-down ${styles.scrollArrow}`} />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={styles.benefitsSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Perché {actionVerb.infinitive} l'app?</h2>
            <p className={styles.sectionSubtitle}>
              Tre buoni motivi per avere Giochini sempre a portata di mano
            </p>
          </div>

          <div className={styles.benefitsGrid}>
            {BENEFITS.map((benefit, index) => (
              <div key={index} className={styles.benefitCard}>
                <div
                  className={styles.benefitIconWrapper}
                  style={{ background: benefit.gradient }}
                >
                  <FaIcon className={`fa-solid ${benefit.icon} ${styles.benefitIcon}`} />
                </div>
                <h3 className={styles.benefitTitle}>{benefit.title}</h3>
                <p className={styles.benefitDescription}>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Streak Story Section */}
      <section className={styles.storySection}>
        <div className={styles.storyCard}>
          <div className={styles.streakVisual}>
            <div className={`${styles.streakDay} ${styles.streakDayCompleted}`}>🔥</div>
            <div className={`${styles.streakDay} ${styles.streakDayCompleted}`}>🔥</div>
            <div className={`${styles.streakDay} ${styles.streakDayCompleted}`}>🔥</div>
            <div className={`${styles.streakDay} ${styles.streakDayCompleted}`}>🔥</div>
            <div className={`${styles.streakDay} ${styles.streakDayCompleted}`}>🔥</div>
            <div className={`${styles.streakDay} ${styles.streakDayPending}`}>?</div>
            <div className={`${styles.streakDay} ${styles.streakDayPending}`}>?</div>
          </div>

          <h2 className={styles.storyTitle}>Mantieni accessa la tua fiamma</h2>
          <p className={styles.storyText}>
            Ogni giorno che giochi, alimenti la tua passione per l'italiano.
            Più la fiamma arde, più salda diventa la tua abitudine.
          </p>
          <p className={styles.storyText}>
            Avere l'app sulla home ti ricorda di tornare, giorno dopo giorno.
          </p>
          <p className={styles.storyHighlight}>
            Il trucco? Mai saltare due giorni di fila.
          </p>
        </div>
      </section>

      {/* Instructions Section */}
      <section id="instructions" className={styles.instructionsSection}>
        <div className={styles.instructionsCard}>
          <div className={styles.instructionsHeader}>
            <div className={styles.instructionsIconWrapper}>
              <FaIcon className="fa-solid fa-mobile-screen-button" />
            </div>
            <h2 className={styles.instructionsTitle}>
              {strategy?.title || 'Aggiungi alla home'}
            </h2>
            <p className={styles.instructionsDescription}>
              {strategy?.description || 'Segui questi passaggi per avere sempre l\'app a portata di mano.'}
            </p>
          </div>

          {/* PWA Prompt Button */}
          {strategy?.type === 'pwaPrompt' && installAvailable ? (
            <div className={styles.buttonContainer}>
              <button
                className={styles.installButton}
                onClick={handleInstallClick}
                disabled={installing}
              >
                {installing ? (
                  <>
                    <FaIcon className="fa-solid fa-spinner fa-spin" />
                    <span>Installazione...</span>
                  </>
                ) : (
                  <>
                    <FaIcon className="fa-solid fa-download" />
                    <span>{strategy.buttonText || 'Installa'}</span>
                  </>
                )}
              </button>
            </div>
          ) : strategy?.steps ? (
            <>
              <ol className={styles.stepsList}>
                {strategy.steps.map((step, index) => (
                  <li key={index} className={styles.step}>
                    <span className={styles.stepNumber}>{index + 1}</span>
                    <span className={styles.stepIcon}>
                      <FaIcon className={`fa-solid ${step.icon}`} />
                    </span>
                    <span className={styles.stepText}>{step.text}</span>
                  </li>
                ))}
              </ol>

              {/* Visual guide for iOS */}
              {strategy.type === 'shareSheetInstructions' && (
                <div className={styles.visualGuide}>
                  <div className={styles.iosShareIcon}>
                    <FaIcon className="fa-solid fa-arrow-up-from-bracket" />
                  </div>
                  <span className={styles.visualGuideText}>
                    Cerca questo pulsante nella barra del browser
                  </span>
                </div>
              )}
            </>
          ) : null}
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className={styles.bottomCtaSection}>
        <h2 className={styles.bottomCtaTitle}>
          Pronto a iniziare?
        </h2>
        <p className={styles.bottomCtaText}>
          {strategy?.type === 'pwaPrompt' && installAvailable
            ? 'Un click e sei pronto per costruire la tua streak.'
            : 'Segui i passaggi qui sopra e inizia il tuo percorso.'}
        </p>

        {strategy?.type === 'pwaPrompt' && installAvailable && (
          <button
            className={styles.ctaButton}
            onClick={handleInstallClick}
            disabled={installing}
          >
            {installing ? (
              <>
                <FaIcon className="fa-solid fa-spinner fa-spin" />
                <span>Installazione...</span>
              </>
            ) : (
              <>
                <FaIcon className="fa-solid fa-download" />
                <span>{actionVerb.imperative} Giochini</span>
              </>
            )}
          </button>
        )}

        <Link to="/" className={styles.backLink}>
          <FaIcon className="fa-solid fa-arrow-left" />
          <span>Torna alla home</span>
        </Link>
      </section>
    </PageShell>
  );
};

export default InstallPage;
